import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Auth.css';

function MfaSetup() {
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [showManualKey, setShowManualKey] = useState(false);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);
  const setupCalledRef = useRef(false);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (setupCalledRef.current) return;
    setupCalledRef.current = true;

    api.post('/api/auth/mfa/setup')
      .then((data) => {
        setQrCode(data.qrCode);
        setManualKey(data.secret);
      })
      .catch((err) => {
        setError(err.response?.data?.error?.message || 'Failed to initialize MFA setup.');
      })
      .finally(() => setSetupLoading(false));
  }, [user, navigate]);

  const handleDigitChange = (index, value) => {
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, '').slice(0, 6).split('');
      const newDigits = [...digits];
      pasted.forEach((d, i) => {
        if (index + i < 6) newDigits[index + i] = d;
      });
      setDigits(newDigits);
      const nextIndex = Math.min(index + pasted.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = digits.join('');

    if (code.length !== 6) {
      setError('Please enter all 6 digits.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await api.post('/api/auth/mfa/confirm-setup', { code });
      updateUser({ mfaEnabled: true });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Invalid code. Please try again.');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (user) {
      localStorage.setItem(`mfa_setup_dismissed_${user.id}`, 'true');
    }
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        <div className="auth-logo">
          <img src="/hd-plus-rcm-logo.png" alt="HD Plus RCM Logo" className="auth-logo-img" />
        </div>

        {success ? (
          <div className="success-card-content">
            <div className="success-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2>MFA Enabled</h2>
            <p>Your account is now protected with two-factor authentication. You&apos;ll need your authenticator app each time you sign in.</p>
            <button className="auth-button" style={{ marginTop: 24 }} onClick={() => navigate('/')}>
              Continue to Dashboard
            </button>
          </div>
        ) : (
          <>
            <div className="mfa-setup-header">
              <div className="mfa-setup-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>
              <h2 className="mfa-setup-title">Secure your account</h2>
              <p className="mfa-setup-subtitle">
                Add an extra layer of security with two-factor authentication. You&apos;ll need an authenticator app like Google Authenticator or Authy.
              </p>
            </div>

            {error && (
              <div className="auth-error" style={{ marginBottom: 20 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {setupLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <div className="loading-spinner" />
              </div>
            ) : (
              <>
                <div className="mfa-setup-step">
                  <div className="mfa-step-number">1</div>
                  <div className="mfa-step-content">
                    <h3>Scan QR code</h3>
                    <p>Open your authenticator app and scan the code below.</p>
                  </div>
                </div>

                {qrCode && (
                  <div className="mfa-qr-container">
                    <img src={qrCode} alt="MFA QR Code" className="mfa-qr-code" />
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setShowManualKey(!showManualKey)}
                      style={{ marginTop: 12 }}
                    >
                      {showManualKey ? 'Hide manual key' : "Can't scan? Enter key manually"}
                    </button>
                    {showManualKey && (
                      <div className="mfa-manual-key">
                        <code>{manualKey}</code>
                      </div>
                    )}
                  </div>
                )}

                <div className="mfa-setup-step" style={{ marginTop: 24 }}>
                  <div className="mfa-step-number">2</div>
                  <div className="mfa-step-content">
                    <h3>Enter verification code</h3>
                    <p>Type the 6-digit code from your authenticator app.</p>
                  </div>
                </div>

                <form onSubmit={handleVerify}>
                  <div className="mfa-code-inputs" style={{ margin: '20px 0' }}>
                    {digits.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        className="mfa-digit"
                        value={digit}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>

                  <button type="submit" className="auth-button" disabled={loading}>
                    {loading && <span className="btn-spinner" />}
                    {loading ? 'Verifying...' : 'Enable MFA'}
                  </button>
                </form>

                <div className="mfa-skip">
                  <button type="button" className="skip-button" onClick={handleSkip}>
                    Skip for now
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="auth-page-footer">
        <p>&copy; 2026 Revenue Cycle Management. All rights reserved.</p>
      </div>
    </div>
  );
}

export default MfaSetup;
